import { Button, Col, Pagination, Row, Space, Spin, Table } from 'antd';
import React, { useState } from 'react';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    InfoCircleOutlined,
    NotificationOutlined,
    PauseCircleOutlined,
    PlusCircleOutlined,
    QuestionCircleOutlined,
    UserOutlined,
} from '@ant-design/icons';
import PopConfirm from '../../../shared/Components/PopConfirm';
import {
    CITRIN_DELETE_REQUEST_BOOKING,
    DeleteRequestBookingRequest,
    DeleteRequestBookingResponse,
} from '../../../GraphQL';
import { useMutation } from '@apollo/client';
import { useSortedRequestBookingQuery } from '../hooks/useSortedRequestBookingQuery';
import { RequestBooking } from '../../../shared/types';
import { useCurrentRequestBooking } from '../hooks/useCurrentRequestBooking';
import RequestBookingEditModal from './RequestBookingEditModal';
import { RequestBookingFilter } from './RequestBookingFilter';
import { useUserState } from '../../../Hooks/useUserState';
import { useGetOperatorQuery } from '../../../GraphQL/hooks/useGetOperatorQuery';
import { convertDateToFormat } from '../../../shared/Utils';

const BookingTable = () => {
    const { sortedRequestBookings: requestBookings, refetch, loading, ...pagination } = useSortedRequestBookingQuery();
    const [deleteMutation] = useMutation<DeleteRequestBookingResponse, DeleteRequestBookingRequest>(
        CITRIN_DELETE_REQUEST_BOOKING
    );
    const { setCurrentRequestBooking } = useCurrentRequestBooking();
    const [isEditModalVisible, setEditModalVisible] = useState(false);
    const { operatorId } = useUserState();
    const { data: operatorData } = useGetOperatorQuery(operatorId);
    /*const handleSetPushNotificationRequestBooking = (requestBooking: RequestBooking) => {
        setIsLoading(true)
        pushNotificationCampaignMutation({ variables: { id: requestBooking._id } })
            .then(() => {
                refetch();
            })
            .catch((err) => console.error(err))
            .finally(() => {
                setIsLoading(false)
            });
    };*/

    const handleEditRequestBooking = (requestBooking: RequestBooking) => {
        setCurrentRequestBooking(requestBooking);
        setEditModalVisible(true);
    };

    const handleRemoveRequestBooking = (requestBooking: RequestBooking) => {
        setIsLoading(true);
        deleteMutation({ variables: { id: requestBooking._id } })
            .then(() => {
                refetch();
            })
            .catch((err) => console.error(err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const [isLoading, setIsLoading] = useState(false);

    //  const isAdmin = !currentOperator.operatorId == operator.type != 'STASIA Mobility';;

    const columns = [
        {
            title: 'ID',
            dataIndex: 'logicalId',
            key: 'logicalId',
            sorter: (a, b) => a.logicalId - b.logicalId,
            width: 80,
        },
        {
            title: 'Passenger Name',
            dataIndex: 'passengerName',
            key: 'passengerName',
        },
        {
            title: 'PickUp Location',
            dataIndex: 'pickUpLocation',
            key: 'pickupLocation',
        },
        {
            title: 'DropOff Location',
            dataIndex: 'dropOffLocation',
            key: 'dropOffLocation',
        },
        {
            title: 'Trip Date',
            dataIndex: 'tripDate',
            key: 'tripDate',

            sorter: (a, b) => a.tripDate - b.tripDate,
            render: (text, record) => {
                return convertDateToFormat(record.tripDate, 'DD-MM-YYYY');
            },
        },
        ...(operatorData?.type === 'STASIA Mobility'
            ? [
                  {
                      title: 'Operator Request',
                      dataIndex: 'operatorId',
                      key: 'operatorId',
                      render: (operator) => operator?.name,
                  },
              ]
            : []),
        {
            title: 'Request Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const iconMap = {
                    Planned: <ClockCircleOutlined style={{ color: 'orange' }} />,
                    Assigned: <UserOutlined style={{ color: 'blue' }} />,
                    Booked: <CheckCircleOutlined style={{ color: 'green' }} />,
                    Cancelled: <CloseCircleOutlined style={{ color: 'red' }} />,
                };

                const icon = iconMap[status];

                return (
                    <span style={{ color: icon ? icon.props.style.color : 'inherit' }}>
                        {icon} {status}
                    </span>
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, requestBooking) => (
                <Space size="middle">
                    <PopConfirm
                        title={'Delete this request'}
                        description={'Are you sure to delete this request?'}
                        onConfirmTask={() => handleRemoveRequestBooking(requestBooking)}
                        popIcon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        actionIcon={<DeleteOutlined style={{ fontSize: '16px', color: 'red' }} />}
                    />
                    <a href="#" onClick={() => handleEditRequestBooking(requestBooking)}>
                        <EditOutlined style={{ fontSize: '16px', color: 'green' }} />
                    </a>
                    <Button
                        icon={<NotificationOutlined title="Assign" style={{ fontSize: '16px', color: 'green' }} />}
                        size={'middle'}
                        onClick={() => console.log('Assign request booking')}
                    >
                        Assign
                    </Button>
                </Space>
            ),
        },
    ];

    const Paginator = () => (
        <Row justify={'center'}>
            <Col>
                <Pagination
                    size="default"
                    total={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    showTotal={(total) => `Total Bookings Request ${total}`}
                    onChange={(newPage) => pagination.setPage(newPage)}
                    showQuickJumper={true}
                    showSizeChanger={false}
                    showLessItems={false}
                />
            </Col>
        </Row>
    );

    return (
        <>
            {isEditModalVisible && (
                <RequestBookingEditModal
                    updateList={refetch}
                    isOpen={isEditModalVisible}
                    onRequestClose={setEditModalVisible}
                />
            )}
            <Row gutter={[16, 6]} style={{ paddingBottom: 10, paddingTop: 20 }}>
                <RequestBookingFilter />
            </Row>
            <div style={{ flex: '1 1 auto' }}>
                <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                    <Col xs={24}>
                        <Spin spinning={isLoading || loading}>
                            <Table
                                dataSource={requestBookings}
                                columns={columns}
                                pagination={false}
                                rowKey={() => Math.random().toString()}
                                scroll={{ x: 1500 }}
                            ></Table>
                        </Spin>
                    </Col>
                </Row>
                <Paginator />
            </div>
        </>
    );
};

export default BookingTable;
